const FRAME_SIZE = 960;
const SAMPLE_RATE = 16000;
const MEDIA_PROTOCOL_V2 = 'meowcaller.media.v2';
const MEDIA_MAGIC = new Uint8Array([77, 67, 86, 50]);
const MEDIA_HEADER_BYTES = 9;
const MEDIA_AUDIO = 1;
const MEDIA_VIDEO = 2;
const MAX_VIDEO_ACCESS_UNIT_BYTES = 4 * 1024 * 1024;
const AVC_MAX_WIDTH = 1280;
const AVC_MAX_HEIGHT = 720;

export const toWebSocketUrl = (mediaPath, location = window.location) => {
  const url = new URL(mediaPath, location.origin);
  if (url.protocol === 'http:') url.protocol = 'ws:';
  if (url.protocol === 'https:') url.protocol = 'wss:';
  return url.toString();
};

const toFloat32Array = data => {
  if (data instanceof Float32Array) return data;
  if (data instanceof ArrayBuffer) return new Float32Array(data);
  if (ArrayBuffer.isView(data)) {
    return new Float32Array(
      data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
    );
  }
  return null;
};

const resample = (samples, inputRate, outputRate) => {
  if (inputRate === outputRate) return samples;

  const outputLength = Math.round((samples.length * outputRate) / inputRate);
  const output = new Float32Array(outputLength);
  const ratio = inputRate / outputRate;

  for (let index = 0; index < outputLength; index += 1) {
    output[index] =
      samples[Math.min(Math.floor(index * ratio), samples.length - 1)];
  }

  return output;
};

const hasKeyframeNal = data => {
  for (let index = 0; index + 4 < data.length; index += 1) {
    let payloadIndex = -1;
    if (data[index] === 0 && data[index + 1] === 0 && data[index + 2] === 1) {
      payloadIndex = index + 3;
    } else if (
      data[index] === 0 &&
      data[index + 1] === 0 &&
      data[index + 2] === 0 &&
      data[index + 3] === 1
    ) {
      payloadIndex = index + 4;
    }
    if (payloadIndex >= 0) {
      const nalType = data[payloadIndex] % 32;
      if (nalType === 5 || nalType === 7) return true;
    }
  }
  return false;
};

const avcLevel31Size = (width, height) => {
  const scale = Math.min(1, AVC_MAX_WIDTH / width, AVC_MAX_HEIGHT / height);
  return {
    width: Math.max(2, Math.floor((width * scale) / 2) * 2),
    height: Math.max(2, Math.floor((height * scale) / 2) * 2),
  };
};

const encodeMediaEnvelope = (kind, payload, metadata = {}) => {
  const bytes =
    payload instanceof Uint8Array ? payload : new Uint8Array(payload);
  const metadataBytes = new TextEncoder().encode(
    Object.keys(metadata).length ? JSON.stringify(metadata) : ''
  );
  const encoded = new Uint8Array(
    MEDIA_HEADER_BYTES + metadataBytes.length + bytes.length
  );
  encoded.set(MEDIA_MAGIC, 0);
  encoded[4] = kind;
  new DataView(encoded.buffer).setUint32(5, metadataBytes.length);
  encoded.set(metadataBytes, MEDIA_HEADER_BYTES);
  encoded.set(bytes, MEDIA_HEADER_BYTES + metadataBytes.length);
  return encoded.buffer;
};

const decodeMediaEnvelope = data => {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (bytes.length < MEDIA_HEADER_BYTES) return null;
  for (let index = 0; index < MEDIA_MAGIC.length; index += 1) {
    if (bytes[index] !== MEDIA_MAGIC[index]) return null;
  }
  const dataView = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength
  );
  const metadataLength = dataView.getUint32(5);
  const payloadOffset = MEDIA_HEADER_BYTES + metadataLength;
  if (payloadOffset > bytes.length) return null;

  let metadata = {};
  if (metadataLength > 0) {
    try {
      metadata = JSON.parse(
        new TextDecoder().decode(bytes.slice(MEDIA_HEADER_BYTES, payloadOffset))
      );
    } catch {
      return null;
    }
  }
  return { kind: bytes[4], metadata, payload: bytes.slice(payloadOffset) };
};

export default class WhatsappCallMedia {
  constructor({
    webSocketFactory = (url, protocols) => new WebSocket(url, protocols),
    audioContextFactory = options => {
      const AudioContextConstructor =
        window.AudioContext || window.webkitAudioContext;
      if (!AudioContextConstructor) throw new Error('Audio is unavailable');
      return new AudioContextConstructor(options);
    },
    videoEncoderFactory = config => new window.VideoEncoder(config),
    videoDecoderFactory = config => new window.VideoDecoder(config),
    videoTrackProcessorFactory = track =>
      new window.MediaStreamTrackProcessor({ track }),
    mediaDevices = navigator.mediaDevices,
    location = window.location,
  } = {}) {
    this.webSocketFactory = webSocketFactory;
    this.audioContextFactory = audioContextFactory;
    this.videoEncoderFactory = videoEncoderFactory;
    this.videoDecoderFactory = videoDecoderFactory;
    this.videoTrackProcessorFactory = videoTrackProcessorFactory;
    this.mediaDevices = mediaDevices;
    this.location = location;
    this.socket = null;
    this.audioContext = null;
    this.mediaStream = null;
    this.videoStream = null;
    this.microphoneNode = null;
    this.processorNode = null;
    this.silentGainNode = null;
    this.videoReader = null;
    this.videoEncoder = null;
    this.videoDecoder = null;
    this.videoCanvas = null;
    this.videoFrameSerial = 0;
    this.forceKeyframe = true;
    this.playbackTime = 0;
    this.pendingSamples = new Float32Array(0);
    this.playbackNodes = new Set();
    this.callbacks = {};
    this.isStopping = false;
  }

  connect({ mediaPath, token, onStatus, onError, onDisconnect } = {}) {
    if (!mediaPath || !token) {
      throw new Error('Missing WhatsApp call media credentials');
    }

    if (this.socket || this.microphoneNode || this.processorNode) {
      this.disconnect();
    }
    this.callbacks = { onStatus, onError, onDisconnect };
    this.isStopping = false;

    const socket = this.webSocketFactory(
      toWebSocketUrl(mediaPath, this.location),
      [MEDIA_PROTOCOL_V2, token]
    );
    const isCurrentSocket = () => this.socket === socket;
    socket.binaryType = 'arraybuffer';
    socket.onopen = () => {
      if (!isCurrentSocket()) return;
      this.startMicrophoneCapture().catch(error => {
        this.reportError(error?.message || 'Unable to start microphone');
      });
    };
    socket.onmessage = event => {
      if (!isCurrentSocket()) return;
      this.handleMessage(event.data).catch(error => {
        this.reportError(error?.message || 'Unable to read call media');
      });
    };
    socket.onerror = () => {
      if (!isCurrentSocket()) return;
      this.reportError('Unable to connect call media');
      socket.close();
    };
    socket.onclose = () => {
      if (!isCurrentSocket()) return;
      if (!this.isStopping) {
        this.callbacks.onDisconnect?.('Call media connection closed');
        this.callbacks.onStatus?.({ status: 'disconnected' });
      }
    };
    this.socket = socket;

    return socket;
  }

  sendControl(action, callId, payload = {}) {
    if (this.socket?.readyState !== 1) return false;

    this.socket.send(JSON.stringify({ action, call_id: callId, ...payload }));
    return true;
  }

  async handleMessage(data) {
    if (typeof data === 'string') {
      try {
        this.callbacks.onStatus?.(JSON.parse(data));
      } catch {
        this.reportError('Received invalid call status');
      }
      return;
    }

    const payload = data instanceof Blob ? await data.arrayBuffer() : data;
    const envelope = decodeMediaEnvelope(payload);
    if (!envelope) {
      this.playRemoteFrame(payload);
      return;
    }
    if (envelope.kind === MEDIA_AUDIO) {
      this.playRemoteFrame(envelope.payload);
      return;
    }
    if (envelope.kind === MEDIA_VIDEO) {
      this.decodeRemoteVideo(envelope.payload, envelope.metadata);
    }
  }

  async prepareMicrophone() {
    this.isStopping = false;
    const existingTrack =
      this.mediaStream?.getAudioTracks?.()[0] ||
      this.mediaStream?.getTracks?.()[0];
    if (existingTrack && existingTrack.readyState !== 'ended') {
      return this.mediaStream;
    }

    if (!this.mediaDevices?.getUserMedia) {
      throw new Error('Microphone access is unavailable');
    }

    const audioContext = this.ensureAudioContext();
    await audioContext.resume?.();
    const mediaStream = await this.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    if (this.isStopping) {
      mediaStream.getTracks().forEach(track => track.stop());
      throw new Error('Call media was stopped before microphone became ready');
    }

    this.mediaStream = mediaStream;
    return mediaStream;
  }

  async startVideoCapture({ screen = false } = {}) {
    const existingTrack = this.videoStream?.getVideoTracks?.()[0];
    if (existingTrack && existingTrack.readyState !== 'ended') {
      return this.videoStream;
    }

    const getStream = screen
      ? this.mediaDevices?.getDisplayMedia
      : this.mediaDevices?.getUserMedia;
    if (!getStream) {
      throw new Error(
        screen
          ? 'Screen sharing is unavailable'
          : 'Camera access is unavailable'
      );
    }
    const stream = await getStream.call(
      this.mediaDevices,
      screen
        ? { video: true, audio: false }
        : {
            video: {
              width: { ideal: AVC_MAX_WIDTH, max: AVC_MAX_WIDTH },
              height: { ideal: AVC_MAX_HEIGHT, max: AVC_MAX_HEIGHT },
              frameRate: { ideal: 15, max: 15 },
            },
            audio: false,
          }
    );
    if (this.isStopping) {
      stream.getTracks().forEach(track => track.stop());
      throw new Error('Call media was stopped before video became ready');
    }

    await this.stopVideoCapture();
    this.videoStream = stream;
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error('Video capture did not provide a track');
    this.videoEncoder = this.videoEncoderFactory({
      output: chunk => this.sendEncodedVideo(chunk),
      error: error =>
        this.reportError(error?.message || 'Unable to encode video'),
    });
    this.videoReader =
      this.videoTrackProcessorFactory(track).readable.getReader();
    this.pumpVideo(this.videoReader, this.videoEncoder).catch(error => {
      this.reportError(error?.message || 'Unable to capture video');
    });
    return stream;
  }

  async stopVideoCapture() {
    await this.videoReader?.cancel?.().catch(() => {});
    if (this.videoEncoder?.state !== 'closed') this.videoEncoder?.close?.();
    this.videoStream?.getTracks().forEach(track => track.stop());
    this.videoReader = null;
    this.videoEncoder = null;
    this.videoStream = null;
    this.forceKeyframe = true;
  }

  pumpVideo(
    reader,
    encoder,
    state = { frameIndex: 0, encodedWidth: 0, encodedHeight: 0 }
  ) {
    return reader
      .read()
      .then(({ value: frame, done }) => {
        if (done) return undefined;
        const size = avcLevel31Size(frame.displayWidth, frame.displayHeight);
        if (
          size.width !== state.encodedWidth ||
          size.height !== state.encodedHeight
        ) {
          state.encodedWidth = size.width;
          state.encodedHeight = size.height;
          encoder.configure({
            codec: 'avc1.42E01F',
            avc: { format: 'annexb' },
            width: size.width,
            height: size.height,
            framerate: 15,
            bitrate: Math.max(
              250000,
              Math.min(2000000, size.width * size.height * 2)
            ),
            latencyMode: 'realtime',
          });
          this.forceKeyframe = true;
        }
        if (encoder.state === 'configured' && encoder.encodeQueueSize < 2) {
          const keyFrame = this.forceKeyframe || state.frameIndex % 15 === 0;
          this.forceKeyframe = false;
          encoder.encode(frame, { keyFrame });
          state.frameIndex += 1;
        }
        frame.close();
        return this.pumpVideo(reader, encoder, state);
      })
      .catch(error => {
        if (this.videoReader !== reader) return undefined;
        this.reportError(error?.message || 'Unable to capture video');
        return this.stopVideoCapture();
      });
  }

  sendEncodedVideo(chunk) {
    if (this.socket?.readyState !== 1) return;
    const payload = new Uint8Array(chunk.byteLength);
    chunk.copyTo(payload);
    if (payload.length > MAX_VIDEO_ACCESS_UNIT_BYTES) {
      this.reportError('Encoded video frame is too large');
      return;
    }
    try {
      this.socket.send(encodeMediaEnvelope(MEDIA_VIDEO, payload));
    } catch (error) {
      this.reportError(error?.message || 'Unable to send video');
      this.socket.close();
    }
  }

  setVideoCanvas(canvas) {
    this.videoCanvas = canvas || null;
    if (!canvas && this.videoDecoder?.state !== 'closed') {
      this.videoDecoder.close();
      this.videoDecoder = null;
    }
  }

  requestVideoKeyframe() {
    this.forceKeyframe = true;
  }

  decodeRemoteVideo(accessUnit, metadata = {}) {
    if (!this.videoCanvas) return;
    if (!this.videoDecoder || this.videoDecoder.state === 'closed') {
      this.videoDecoder = this.videoDecoderFactory({
        output: frame => {
          this.drawRemoteFrame(frame, metadata);
          frame.close();
        },
        error: error => {
          this.videoFrameSerial = 0;
          this.videoDecoder = null;
          this.reportError(error?.message || 'Unable to decode remote video');
        },
      });
      this.videoDecoder.configure({
        codec: 'avc1.42E01F',
        optimizeForLatency: true,
      });
    }
    const keyFrame = hasKeyframeNal(accessUnit);
    if (!keyFrame && this.videoFrameSerial === 0) return;
    try {
      this.videoDecoder.decode(
        new window.EncodedVideoChunk({
          type: keyFrame ? 'key' : 'delta',
          timestamp: performance.now() * 1000,
          data: accessUnit,
        })
      );
      this.videoFrameSerial += 1;
    } catch (error) {
      this.videoFrameSerial = 0;
      this.videoDecoder?.close?.();
      this.videoDecoder = null;
      this.reportError(error?.message || 'Unable to decode remote video');
    }
  }

  drawRemoteFrame(frame, metadata = {}) {
    const canvas = this.videoCanvas;
    const context = canvas?.getContext?.('2d');
    if (!context) return;
    const orientation = (((metadata.orientation || 0) % 4) + 4) % 4;
    const portrait = orientation % 2 === 1;
    canvas.width = portrait ? frame.displayHeight : frame.displayWidth;
    canvas.height = portrait ? frame.displayWidth : frame.displayHeight;
    context.save();
    if (orientation === 1) {
      context.translate(canvas.width, 0);
      context.rotate(Math.PI / 2);
    } else if (orientation === 2) {
      context.translate(canvas.width, canvas.height);
      context.rotate(Math.PI);
    } else if (orientation === 3) {
      context.translate(0, canvas.height);
      context.rotate(-Math.PI / 2);
    }
    context.drawImage(frame, 0, 0, frame.displayWidth, frame.displayHeight);
    context.restore();
  }

  async startMicrophoneCapture() {
    try {
      const audioContext = this.ensureAudioContext();
      const mediaStream = await this.prepareMicrophone();

      if (this.isStopping || this.processorNode) return;

      this.microphoneNode = audioContext.createMediaStreamSource(mediaStream);
      this.processorNode = audioContext.createScriptProcessor(4096, 1, 1);
      this.silentGainNode = audioContext.createGain();
      this.silentGainNode.gain.value = 0;
      this.processorNode.onaudioprocess = event => {
        const samples = event.inputBuffer.getChannelData(0);
        this.sendMicrophoneSamples(samples);
      };

      this.microphoneNode.connect(this.processorNode);
      this.processorNode.connect(this.silentGainNode);
      this.silentGainNode.connect(audioContext.destination);
    } catch (error) {
      this.reportError(error?.message || 'Microphone access was denied');
      this.socket?.close();
    }
  }

  sendMicrophoneSamples(samples) {
    if (this.socket?.readyState !== 1 || !this.audioContext) return;

    const resampledSamples = resample(
      samples,
      this.audioContext.sampleRate,
      SAMPLE_RATE
    );
    const bufferedSamples = new Float32Array(
      this.pendingSamples.length + resampledSamples.length
    );
    bufferedSamples.set(this.pendingSamples);
    bufferedSamples.set(resampledSamples, this.pendingSamples.length);

    let offset = 0;
    while (bufferedSamples.length - offset >= FRAME_SIZE) {
      const frame = bufferedSamples.slice(offset, offset + FRAME_SIZE);
      try {
        this.socket.send(encodeMediaEnvelope(MEDIA_AUDIO, frame.buffer));
      } catch (error) {
        this.reportError(error?.message || 'Unable to send microphone audio');
        this.socket.close();
        return;
      }
      offset += FRAME_SIZE;
    }
    this.pendingSamples = bufferedSamples.slice(offset);
  }

  playRemoteFrame(data) {
    const samples = toFloat32Array(data);
    if (!samples?.length) return;

    try {
      const audioContext = this.ensureAudioContext();
      const buffer = audioContext.createBuffer(1, samples.length, SAMPLE_RATE);
      if (buffer.copyToChannel) buffer.copyToChannel(samples, 0);
      else buffer.getChannelData(0).set(samples);

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.onended = () => this.playbackNodes.delete(source);
      this.playbackNodes.add(source);

      const startAt = Math.max(audioContext.currentTime, this.playbackTime);
      source.start(startAt);
      this.playbackTime = startAt + buffer.duration;
    } catch (error) {
      this.reportError(error?.message || 'Unable to play call audio');
    }
  }

  disconnect() {
    this.isStopping = true;
    this.microphoneNode?.disconnect();
    this.processorNode?.disconnect();
    this.silentGainNode?.disconnect();
    if (this.processorNode) this.processorNode.onaudioprocess = null;
    this.playbackNodes.forEach(source => source.stop?.());
    this.playbackNodes.clear();
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.stopVideoCapture().catch(() => {});
    this.videoDecoder?.close?.();
    this.audioContext?.close?.();
    this.socket?.close?.();

    this.socket = null;
    this.audioContext = null;
    this.mediaStream = null;
    this.videoCanvas = null;
    this.videoDecoder = null;
    this.microphoneNode = null;
    this.processorNode = null;
    this.silentGainNode = null;
    this.pendingSamples = new Float32Array(0);
    this.playbackTime = 0;
    this.videoFrameSerial = 0;
  }

  ensureAudioContext() {
    if (!this.audioContext) {
      this.audioContext = this.audioContextFactory({ sampleRate: SAMPLE_RATE });
    }
    return this.audioContext;
  }

  reportError(message) {
    this.callbacks.onError?.(message);
  }
}

export const mediaProtocol = {
  MEDIA_PROTOCOL_V2,
  MEDIA_AUDIO,
  MEDIA_VIDEO,
  encodeMediaEnvelope,
  decodeMediaEnvelope,
};
